'use client';
import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import type { ApexOptions } from 'apexcharts';
import { token } from '@terra/ui-styled-system';
import { useThemeMode } from '@terra/ui-design-system';
import type { LineTooltipParams } from './format';

const ReactApexChart = dynamic(() => import('react-apexcharts'), {
  ssr: false,
});

export interface HeatmapChartDataPoint {
  x: string;
  y: number;
}

export interface HeatmapChartProps {
  /**
   * Array of data series for the heatmap
   */
  series: {
    name: string;
    data: HeatmapChartDataPoint[];
  }[];
  /**
   * Optional title for the chart
   */
  title?: string;
  /**
   * Height of the chart
   */
  height?: number | string;
  /**
   * Width of the chart
   */
  width?: number | string;
  /**
   * Color range for the heatmap
   */
  colors?: string[];
  /**
   * Whether to show data labels on cells
   */
  showDataLabels?: boolean;
  /**
   * Custom tooltip renderer
   */
  tooltipCustom?: (options: LineTooltipParams) => string;
  /**
   * Custom formatter for tooltip values
   */
  tooltipFormatter?: (value: number) => string;
  /**
   * Whether to show the legend
   */
  showLegend?: boolean;
  /**
   * Position of the legend
   */
  legendPosition?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Callback function when a data point is clicked
   */
  onDataPointClick?: (
    dataPointIndex: number,
    seriesIndex: number,
    value: number,
  ) => void;
}

type HeatmapColorScaleRange = {
  from: number;
  to: number;
  color: string;
  name: string;
};

function buildHeatmapColorScaleRanges({
  maxValue,
  colors,
}: {
  maxValue: number;
  colors: string[];
}): HeatmapColorScaleRange[] {
  const noneColor = token('colors.gray.2');
  if (maxValue <= 0) {
    return [
      {
        from: 0,
        to: 0,
        color: noneColor,
        name: 'None',
      },
    ];
  }

  const palette =
    colors.length > 0
      ? colors
      : [
          token('colors.terra.3'),
          token('colors.terra.6'),
          token('colors.terra.8'),
          token('colors.terra.9'),
        ];
  const bucketCount = Math.min(palette.length, maxValue);
  const bucketSize = Math.max(1, Math.ceil(maxValue / bucketCount));

  return [
    {
      from: 0,
      to: 0,
      color: noneColor,
      name: 'None',
    },
    ...palette.slice(0, bucketCount).map((color, index) => {
      const from = index * bucketSize + 1;
      const to =
        index === bucketCount - 1 ? maxValue : (index + 1) * bucketSize;
      return {
        from,
        to,
        color,
        name: from === to ? String(from) : `${from}–${to}`,
      };
    }),
  ];
}

export function HeatmapChart({
  series,
  title = '',
  height = 350,
  width = '100%',
  colors = [
    token('colors.terra.3'),
    token('colors.terra.6'),
    token('colors.terra.8'),
    token('colors.terra.9'),
  ],
  showDataLabels = true,
  tooltipCustom,
  tooltipFormatter,
  showLegend = false,
  legendPosition = 'bottom',
  onDataPointClick,
}: HeatmapChartProps) {
  const { mode: themeMode } = useThemeMode();

  const options = useMemo<ApexOptions>(() => {
    if (!series || !series.length) {
      console.error('HeatmapChart: Invalid or empty series data');
      return {
        chart: {
          type: 'heatmap',
          toolbar: {
            show: false,
          },
        },
      };
    }

    const maxValue = series.reduce((seriesMax, seriesItem) => {
      const itemMax = seriesItem.data.reduce(
        (pointMax, point) =>
          Math.max(pointMax, Number.isFinite(point.y) ? point.y : 0),
        0,
      );
      return Math.max(seriesMax, itemMax);
    }, 0);

    return {
      chart: {
        type: 'heatmap',
        toolbar: {
          show: false,
        },
        foreColor: 'currentColor',
        animations: {
          enabled: true,
          speed: 300,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
        events: {
          dataPointSelection: onDataPointClick
            ? (event, chartContext, opts) => {
                const { dataPointIndex, seriesIndex } = opts;
                const value = series[seriesIndex].data[dataPointIndex].y;
                onDataPointClick(dataPointIndex, seriesIndex, value);
              }
            : undefined,
        },
      },
      colors: colors,
      dataLabels: {
        enabled: showDataLabels,
        style: {
          fontSize: '12px',
          fontFamily: 'inherit',
          fontWeight: 'normal',
          colors: ['#fff'],
        },
      },
      title: {
        text: title,
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 'normal',
        },
      },
      tooltip: {
        theme: themeMode === 'dark' ? 'dark' : 'light',
        enabled: true,
        custom: tooltipCustom,
        y: {
          formatter: tooltipFormatter,
        },
      },
      legend: {
        show: showLegend,
        position: legendPosition,
        fontSize: '14px',
        fontFamily: 'inherit',
        labels: {
          useSeriesColors: false,
        },
      },
      plotOptions: {
        heatmap: {
          enableShades: false,
          radius: 0,
          useFillColorAsStroke: false,
          colorScale: {
            ranges: buildHeatmapColorScaleRanges({ maxValue, colors }),
          },
        },
      },
      stroke: {
        width: 1,
        colors: [token('colors.border.default')],
      },
      grid: {
        borderColor: token('colors.border.default'),
        strokeDashArray: 0,
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
    };
  }, [
    series,
    colors,
    showDataLabels,
    title,
    tooltipCustom,
    tooltipFormatter,
    showLegend,
    legendPosition,
    themeMode,
    onDataPointClick,
  ]);

  if (!options) {
    return null;
  }

  return (
    <ReactApexChart
      options={options}
      series={series}
      type="heatmap"
      height={height}
      width={width}
    />
  );
}

export default HeatmapChart;
